/* eslint-disable import/first */
import React, { useState, useMemo, Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  ContractListProvider,
  useContractList,
  NEXT_PAGE, PREV_PAGE,
  SELECT_ALL_CONTRACTS,
  SET_META,
  SET_SELECTED_CONTRACTS
} from 'context/contracts'
import { useProfileState, useProfileAbility } from 'context/profile'
import { Breadcrumb, BreadcrumbItem, Dropdown, DropdownMenu, DropdownToggle, DropdownItem, Nav, Navbar, Table, UncontrolledDropdown, Row, Col, Container, CustomInput, Media, Spinner, Card, CardBody, CardFooter, Button } from 'reactstrap';
import ContractList from './Contract';
import FolderList from './Folder';
import HeaderToolbar from './HeaderToolbar';
import MiniDashboards from './Minidashboard';
import Pagination from 'components/Shared/Paginator';
// import Table from '../Table/Table'
import useLocalStorage from 'hooks/useLocalStorage';
import { useHistory } from 'react-router-dom';
import { archiverFolder } from 'app-constants';
import TratoLoader from 'components/Shared/TratoLoader';

const filters = [
  'all',
  'draft',
  'active',
  'valid',
  // 'archived',
  'pending',
  'signed',
  'to_sign',
  'canceled',
  'finalized',
  'in_progress',
  'received'];

const defaultColumns = [
  'creator',
  'participants',
  'created',
  'days',
  'status',
  'signature',
  'user'
]

function Caret({ sortDirection, sortBy, selectionName, handleSort }) {
  return (
    <Fragment>
      {sortBy === selectionName &&
        <span onClick={() => handleSort(sortBy)}><i className={clsx('fas', sortDirection === '1' ? 'fa-caret-down' : 'fa-caret-up')} onClick={() => handleSort(sortBy)}></i> </span>
      }
    </Fragment>
  )
}

function Loading() {
  const { t } = useTranslation();
  return (
    <tr className="absolute-center">
      <Spinner size="sm"></Spinner>{' '}{t('contracts.listing.loading')}
    </tr>
  )
}

function ReactContracts() {

  const { t } = useTranslation();
  const { profile } = useProfileState()
  const [state, dispatch] = useContractList()
  const history = useHistory()
  const [listOnlyColumnsToShow, setListOnlyColumnsToShow] = useLocalStorage('list-only-columns', defaultColumns);
  const [columnsToShow, setColumnsToShow] = useLocalStorage('contract-list-columns', defaultColumns);
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false)
  const profileAbility = useProfileAbility();

  const headerTitle = state.currentRecord?.name ? state.currentRecord.name : state.meta.listOnly ? t('incoming.title') : t('contracts.title');
  const columns = state.meta.listOnly ? listOnlyColumnsToShow : columnsToShow;
  const setColumns = state.meta.listOnly ? setListOnlyColumnsToShow : setColumnsToShow;
  const sortBy = state.meta.sortBy;
  const sortDirection = state.meta.sortDirection;

  const handleColumnChecked = (evt) => {
    evt.stopPropagation();
    const target = evt.target;

    if (target.checked) {
      setColumns(columns => [...columns, target.name])
    } else {
      setColumns(columns => columns.filter(c => c !== target.name))
    }
  }

  const toggleColumnConfig = useCallback(() => {
    setIsColumnConfigOpen(open => !open)
  }, [])

  const handleFilter = useCallback((filter) => () => {
    dispatch({
      type: SET_META, meta: {
        url: '/contracts/all',
        page: 1,
        filter,
        sortBy,
        sortDirection
      }
    })
  }, [dispatch, sortBy, sortDirection])

  const nextPage = useCallback(() => {
    dispatch({ type: NEXT_PAGE })
  }, [dispatch]);

  const previousPage = useCallback(() => {
    dispatch({ type: PREV_PAGE })
  }, [dispatch]);

  const onPageClick = useCallback((page) => {
    dispatch({
      type: SET_META, meta: {
        page
      }
    })
  }, [dispatch]);

  const handleSort = useCallback((sortBy) => {
    dispatch({
      type: SET_META, meta: {
        sortBy
      }
    })
  }, [dispatch]);

  const handleSelectAll = useCallback((evt) => {
    if (evt.target.checked) {
      return dispatch({ type: SELECT_ALL_CONTRACTS })
    }

    dispatch({ type: SET_SELECTED_CONTRACTS, selected: [] })
  }, [dispatch])

  const setPendingByMe = useCallback((evt) => {
    dispatch({
      type: SET_META, meta: {
        pendingByMe: !state.meta.pendingByMe
      }
    })
    dispatch({ type: SET_SELECTED_CONTRACTS, selected: [] })
  }, [dispatch, state.meta.pendingByMe])

  const handleGoToList = useCallback(() => {
    history.push('/contracts/list');
  }, [history]);

  const actualFolder = state.currentFolder?.length && state.currentFolder.find(c => c._id === state.meta?.childOf);

  return (
    <div className="h-100 d-flex flex-column">
      <HeaderToolbar />
      {!profileAbility.can('HIDE_MINIDASHBOARDS') && !state.meta.listOnly && !state.meta.recordId?.length && <MiniDashboards />}
      <div className={!profileAbility.can('HIDE_MINIDASHBOARDS') && !state.meta.listOnly && !state.meta.recordId?.length && 'mt-3'}>
        <div className="d-flex">
          <Breadcrumb tag="nav" className="font-weight-bold align-self-center">
            {state.meta.searchTerm && <BreadcrumbItem> {t(`dashboard.counter.${state.meta.searchTerm}`)} </BreadcrumbItem>}
            <BreadcrumbItem
              hidden={state.meta.searchTerm}
              tag={clsx({ a: !!actualFolder, span: !actualFolder })}
              active={!!actualFolder}
              onClick={handleGoToList}>{headerTitle}
            </BreadcrumbItem>
            {
              actualFolder &&
              <BreadcrumbItem>{actualFolder._id === archiverFolder ? t('contracts.listing.archiver') : actualFolder.name}</BreadcrumbItem>
            }
          </Breadcrumb>
          <div className="ml-auto align-self-center">
            <CustomInput
              hidden={state.meta.searchTerm !== 'pendingContracts'}
              checked={state.meta.pendingByMe}
              onChange={setPendingByMe}
              type="checkbox"
              id="pending-by-me"
              label={t('contracts.listing.pending_by_me')}
            />
            <UncontrolledDropdown direction="left" hidden={state.meta.listOnly}>
              <DropdownToggle caret color="white">
                <span>{t('contracts.search.filter')}: </span>
                <span>{t(`contracts.filter.${state.meta.filter}`)}</span>
              </DropdownToggle>
              <DropdownMenu>
                {
                  filters.map((filter, index) => (
                    <>
                      <DropdownItem key={index} onClick={handleFilter(filter)}>{t(`contracts.filter.${filter}`)}</DropdownItem>
                      <DropdownItem divider />
                    </>
                  ))
                }
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>
      </div>

      <div className="flex-grow-1 mb-3">
        <Card className="border bg-white card-shadow rounded-lg h-100" style={{ minHeight: '250px' }}>
          <CardBody className="p-4 mt-2 h-100" style={{ overflowY: 'hidden' }}>
            <Table size="sm" className="bg-white text-center table-layout-fixed h-100 table-hover" style={{ maxHeight: '50px' }}>
              <thead>
                <tr>
                  <th style={{ width: '10px' }} className="text-center pointer align-middle align-items-center">
                    <CustomInput
                      id="select-all"
                      hidden={state.meta.listOnly && !state.meta.searchTerm}
                      type="checkbox"
                      onChange={handleSelectAll} />
                  </th>
                  <th style={{ width: '70px' }} className="text-center pointer" onClick={() => handleSort('documentId')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='documentId' sortDirection={sortDirection} />Id</th>
                  <th className="text-center pointer text-nowrap text-truncate" style={{ width: '20%' }} onClick={() => handleSort('name')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='name' sortDirection={sortDirection} />{t('contracts.listing.header_name')}</th>
                  {columns.includes('milestone') && profileAbility.can('read', 'Contract', 'milestones') && <th className="text-center text-truncate d-none d-lg-table-cell">{t('contracts.milestones.progress')}</th>}
                  {columns.includes('creator') && <th className="text-center pointer text-truncate" onClick={() => handleSort('creator')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='creator' sortDirection={sortDirection} />{t('contracts.listing.header_creator')}</th>}
                  {columns.includes('user') && <th className="text-center pointer text-truncate d-none d-lg-table-cell" onClick={() => handleSort('user')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='user' sortDirection={sortDirection} />{t('contracts.listing.header_owner')}</th>}
                  {columns.includes('participants') && <th className="text-center pointer text-truncate" onClick={() => handleSort('participants')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='participants' sortDirection={sortDirection} />{t('contracts.listing.participants')}</th>}
                  {columns.includes('entered') && <th className="text-center text-truncate">{t('contracts.listing.entered')}</th>}
                  {columns.includes('created') && <th className="text-center pointer text-truncate" onClick={() => handleSort('createdAt')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='createdAt' sortDirection={sortDirection} />{t('templates.listing.created')}</th>}
                  {columns.includes('days') && <th className="text-center text-truncate  d-none d-lg-table-cell">{t('contracts.listing.ddays')}</th>}
                  {columns.includes('status') && <th className="text-center pointer text-truncate" onClick={() => handleSort('status')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='status' sortDirection={sortDirection} />{t('contracts.listing.header_status')}</th>}
                  {columns.includes('signature') && <th className="text-center pointer text-truncate  d-none d-lg-table-cell" onClick={() => handleSort('signatureType')}><Caret handleSort={handleSort} sortBy={sortBy} selectionName='signatureType' sortDirection={sortDirection} />{t('contracts.listing.header_signature')}</th>}


                  <th style={{ width: '50px' }}>
                    <Dropdown direction="left" isOpen={isColumnConfigOpen} toggle={toggleColumnConfig}>
                      <DropdownToggle tag="span">
                        <i className="fas fa-cog pointer"></i>
                      </DropdownToggle>
                      <DropdownMenu className="contract-actions-menu">
                        <DropdownItem toggle={false} hidden={!profileAbility.can('read', 'Contract', 'milestones')}>
                          <CustomInput label={t('contracts.milestones.title')} type="checkbox" id="milestone" name="milestone" onChange={handleColumnChecked} defaultChecked={columns.includes('milestone')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('contracts.listing.header_creator')} type="checkbox" id="creator" name="creator" onChange={handleColumnChecked} defaultChecked={columns.includes('creator')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('contracts.listing.header_owner')} type="checkbox" id="user" name="user" onChange={handleColumnChecked} defaultChecked={columns.includes('user')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('contracts.listing.participants')} type="checkbox" id="participants" name="participants" onChange={handleColumnChecked} defaultChecked={columns.includes('participants')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('templates.listing.created')} type="checkbox" id="created" name="created" onChange={handleColumnChecked} defaultChecked={columns.includes('created')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('contracts.listing.ddays')} type="checkbox" id="days" name="days" onChange={handleColumnChecked} defaultChecked={columns.includes('days')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('contracts.listing.header_status')} type="checkbox" id="status" name="status" onChange={handleColumnChecked} defaultChecked={columns.includes('status')} />
                        </DropdownItem>
                        <DropdownItem toggle={false}>
                          <CustomInput label={t('contracts.listing.header_signature')} type="checkbox" id="signature" name="signature" onChange={handleColumnChecked} defaultChecked={columns.includes('signature')} />
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </th>
                </tr>
              </thead>
              <tbody>
                <FolderList columns={columns} />
                <ContractList columns={columns} />
              </tbody>
            </Table>
          </CardBody>
          <CardFooter className="text-center" style={{ borderBottomLeftRadius: '22px', borderBottomRightRadius: '22px' }}>
            <Pagination
              {...state.meta}
              isDisabled={state.status === 'loading'}
              onNextPage={nextPage}
              onPrevPage={previousPage}
              onPageClick={onPageClick}
            />
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

function App(props) {
  return (
    <ContractListProvider {...props}>
      <ReactContracts />
    </ContractListProvider>
  )
}

export default App;
